import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  SUBSCRIPTION_TIERS,
  SUBSCRIPTION_VAT_PERCENT,
  tierAllows,
  tierPriceCents,
  type SubscriptionInfo,
  type SubscriptionInterval,
  type SubscriptionTier,
} from "@handvero/shared";
import { api, ApiFailure, errorText } from "../api";
import { Notice } from "../components/ui";
import { formatDate } from "../hooks";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const firstSeats = (t: SubscriptionTier) => (t.id === "business" ? 4 : t.id === "solo" ? 1 : 3);

/** Abo wählen und bezahlen, Zahlungen mit Rechnungsnummer sehen, kündigen. */
export function Abo() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState<SubscriptionInfo | null>(null);
  const [interval, setInterval] = useState<SubscriptionInterval>("month");
  const [seats, setSeats] = useState<Record<string, number>>({ solo: 1, team: 3, business: 4 });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      setInfo(await api.get<SubscriptionInfo>("/api/subscription"));
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) navigate("/anmelden", { replace: true });
    }
  }, [navigate]);
  useEffect(() => {
    void load();
  }, [load]);

  // Nach der Rückkehr von der Bezahlseite kurz nachfragen, bis Mollie die Zahlung gemeldet hat.
  const back = params.get("bezahlt") !== null;
  useEffect(() => {
    if (!back) return;
    const timer = window.setInterval(() => void load(), 3000);
    const stop = window.setTimeout(() => window.clearInterval(timer), 60000);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(stop);
    };
  }, [back, load]);

  async function book(tier: SubscriptionTier) {
    setBusy(tier.id);
    setError("");
    try {
      const r = await api.post<{ checkoutUrl: string }>("/api/subscription/checkout", { tier: tier.id, interval, employees: seats[tier.id] ?? firstSeats(tier) });
      window.location.href = r.checkoutUrl;
    } catch (err) {
      setError(errorText(err));
      setBusy(null);
    }
  }

  async function cancel() {
    if (!window.confirm("Abo wirklich kündigen? Es wird nichts mehr abgebucht. Die bezahlte Zeit bleibt Ihnen erhalten.")) return;
    try {
      await api.post("/api/subscription/cancel");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  if (!info) return <section data-page="abo" />;
  const cur = info.current && (info.current.status === "active" || info.current.status === "canceled") ? info.current : null;
  const paid = info.payments.some((p) => p.status === "paid");

  return (
    <section data-page="abo">
      <div className="wrap stack-lg">
        <div className="stack" style={{ gap: 8 }}>
          <Link to="/kundenbereich" className="btn-link small">
            Zurück zum Kundenbereich
          </Link>
          <span className="eyebrow">Abo</span>
          <h1 style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)" }}>{cur ? "Ihr Abo" : "Welches Paket passt zu Ihnen?"}</h1>
        </div>

        {back && !paid && <Notice kind="info">Danke. Wir warten noch auf die Bestätigung der Zahlung. Das dauert meist nur wenige Sekunden, diese Seite aktualisiert sich selbst.</Notice>}
        {back && paid && <Notice kind="ok">Die Zahlung ist da, Ihr Abo ist freigeschaltet. Die Rechnung haben wir Ihnen per E-Mail geschickt.</Notice>}
        {!info.bookable && <Notice kind="info">Das Abo ist noch nicht buchbar.</Notice>}
        {info.bookable && !info.configured && <Notice kind="info">Die Bezahlung wird gerade eingerichtet. Bitte versuchen Sie es später noch einmal.</Notice>}

        {cur && (
          <div className="card stack">
            <h3>{SUBSCRIPTION_TIERS.find((t) => t.id === cur.tier)?.name}</h3>
            <p>
              {cur.employees} Mitarbeiter, {cur.interval === "month" ? "monatliche" : "jährliche"} Zahlung.{" "}
              {cur.status === "canceled" ? "Gekündigt, Sie nutzen HandVero noch bis" : "Läuft bis"} {formatDate(cur.validUntil)}.
            </p>
            {cur.status === "active" && (
              <button className="btn btn-ghost" onClick={cancel} style={{ justifySelf: "start" }}>
                Abo kündigen
              </button>
            )}
          </div>
        )}

        {!cur && info.bookable && info.configured && (
          <>
            <div className="stack" style={{ gap: 10 }}>
              <div className="abo-switch" role="group" aria-label="Zahlweise">
                <button type="button" className={interval === "month" ? "on" : undefined} aria-pressed={interval === "month"} onClick={() => setInterval("month")}>
                  Monatlich
                </button>
                <button type="button" className={interval === "year" ? "on" : undefined} aria-pressed={interval === "year"} onClick={() => setInterval("year")}>
                  Jährlich, 2 Monate gratis
                </button>
              </div>
              <p className="small muted">
                Alle Preise netto pro Firma, dazu {SUBSCRIPTION_VAT_PERCENT} % Umsatzsteuer. Bezahlt wird sicher über Mollie, zum Beispiel per Kreditkarte oder SEPA-Lastschrift. Sie können jederzeit kündigen.
              </p>
            </div>
            <div className="price-grid">
              {SUBSCRIPTION_TIERS.map((tier) => {
                const n = seats[tier.id] ?? firstSeats(tier);
                const net = tierPriceCents(tier, n, interval);
                return (
                  <div className={`price${tier.id === "team" ? " feat" : ""}`} key={tier.id}>
                    {tier.id === "team" && <span className="tag">Beliebt</span>}
                    <h3>{tier.name}</h3>
                    <div className="amount">
                      {euro(net)} <small>{interval === "month" ? "im Monat" : "im Jahr"}, netto</small>
                    </div>
                    {tier.id !== "solo" && (
                      <label className="stack" style={{ gap: 4 }}>
                        <span className="small">Anzahl Mitarbeiter</span>
                        <select value={n} onChange={(e) => setSeats({ ...seats, [tier.id]: Number(e.target.value) })}>
                          {Array.from({ length: 10 }, (_, i) => i + 1)
                            .filter((x) => tierAllows(tier, x))
                            .map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                        </select>
                      </label>
                    )}
                    <ul className="checks">
                      <li>{tier.id === "solo" ? "1 Mitarbeiter" : tier.id === "business" ? `4 bis ${tier.employeeLimit} Mitarbeiter` : `Bis zu ${tier.employeeLimit} Mitarbeiter`}</li>
                      <li>Angebote, Aufträge und Rechnungen</li>
                      <li>Unterweisungen</li>
                      <li>Windows-Software</li>
                      {tier.extraSeatMonthlyCents > 0 && <li>{euro(tier.extraSeatMonthlyCents)} im Monat je Mitarbeiter ab dem {tier.includedSeats + 1}.</li>}
                    </ul>
                    <button className="btn btn-primary btn-big" onClick={() => book(tier)} disabled={busy !== null}>
                      {busy === tier.id ? "Einen Moment …" : `${tier.name} wählen und bezahlen`}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {error && <Notice kind="bad">{error}</Notice>}

        {info.payments.length > 0 && (
          <div className="card stack">
            <h3>Ihre Zahlungen</h3>
            <ul className="people">
              {info.payments.map((p) => (
                <li key={p.id}>
                  <span className="who">
                    <span>{formatDate(p.date)}</span>
                    <span>{p.status === "paid" ? `bezahlt${p.invoiceNumber ? `, Rechnung ${p.invoiceNumber}` : ""}` : p.status === "open" ? "wartet auf Zahlung" : "nicht bezahlt"}</span>
                  </span>
                  <span className="muted small">{euro(p.grossCents)} brutto</span>
                </li>
              ))}
            </ul>
            <p className="small muted">Zu jeder Zahlung schicken wir Ihnen die Rechnung per E-Mail.</p>
          </div>
        )}
      </div>
    </section>
  );
}
